/** Cliente de la API del operador de plataforma. */
import type {
  ActualizarEstadoAdministradorRequest,
  ActualizarEstadoAdministradorResponse,
  ActualizarLicenciaRequest,
  ActualizarLicenciaResponse,
  ActualizarSubdominioRequest,
  ActualizarSubdominioResponse,
  CrearAdministradorRequest,
  CrearAdministradorResponse,
  CrearOrganismoRequest,
  CrearOrganismoResponse,
  EditarRolesAdministradorRequest,
  EditarRolesAdministradorResponse,
  ListarAdministradoresResponse,
  ListarOrganismosResponse,
  UploadUrlResponse,
} from '@sumarios/shared';
import { config } from './config.js';

async function llamar<T>(
  idToken: string,
  metodo: string,
  ruta: string,
  cuerpo?: unknown,
): Promise<T> {
  const r = await fetch(`${config.apiUrl}${ruta}`, {
    method: metodo,
    headers: {
      authorization: `Bearer ${idToken}`,
      ...(cuerpo !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
  });
  if (!r.ok) {
    const texto = await r.text().catch(() => '');
    throw new Error(`${metodo} ${ruta} → ${r.status}${texto ? `: ${texto}` : ''}`);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/** Sube el archivo directo al bucket usando la URL prefirmada (no lleva el JWT de la API). */
export async function subirArchivo(uploadUrl: string, archivo: File): Promise<void> {
  const r = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': archivo.type },
    body: archivo,
  });
  if (!r.ok) throw new Error(`Subida a S3 falló: ${r.status}`);
}

export const listarOrganismos = (idToken: string) =>
  llamar<ListarOrganismosResponse>(idToken, 'GET', '/organismos');

export const crearOrganismo = (idToken: string, datos: CrearOrganismoRequest) =>
  llamar<CrearOrganismoResponse>(idToken, 'POST', '/organismos', datos);

export const actualizarLicencia = (
  idToken: string,
  orgId: string,
  datos: ActualizarLicenciaRequest,
) => llamar<ActualizarLicenciaResponse>(idToken, 'PATCH', `/organismos/${orgId}/licencia`, datos);

export const actualizarSubdominio = (
  idToken: string,
  orgId: string,
  datos: ActualizarSubdominioRequest,
) =>
  llamar<ActualizarSubdominioResponse>(idToken, 'PATCH', `/organismos/${orgId}/subdominio`, datos);

export const listarAdministradores = (idToken: string, orgId: string) =>
  llamar<ListarAdministradoresResponse>(idToken, 'GET', `/organismos/${orgId}/administradores`);

/** Sin orgId: sirve tanto para el alta de organismo (primer admin) como para admins/auditores de uno existente. */
export const solicitarUploadUrlEvidencia = (
  idToken: string,
  contentType: string,
  tamanoBytes: number,
) =>
  llamar<UploadUrlResponse>(idToken, 'POST', '/evidencia/upload-url', {
    contentType,
    tamanoBytes,
  });

export const crearAdministrador = (
  idToken: string,
  orgId: string,
  datos: CrearAdministradorRequest,
) =>
  llamar<CrearAdministradorResponse>(
    idToken,
    'POST',
    `/organismos/${orgId}/administradores`,
    datos,
  );

export const editarRolesAdministrador = (
  idToken: string,
  orgId: string,
  userId: string,
  datos: EditarRolesAdministradorRequest,
) =>
  llamar<EditarRolesAdministradorResponse>(
    idToken,
    'PATCH',
    `/organismos/${orgId}/administradores/${userId}/roles`,
    datos,
  );

export const actualizarEstadoAdministrador = (
  idToken: string,
  orgId: string,
  userId: string,
  datos: ActualizarEstadoAdministradorRequest,
) =>
  llamar<ActualizarEstadoAdministradorResponse>(
    idToken,
    'PATCH',
    `/organismos/${orgId}/administradores/${userId}/estado`,
    datos,
  );
